/** @jsxImportSource @emotion/react */
import React, { createContext, useContext, useReducer, useCallback, useEffect, ReactNode, useRef } from 'react'
import { 
    QuestionnaireFormData, 
    initialFormData, 
    FAQItem,
    FormContextType
} from '../../common/context/SharedFormTypes'
import { formReducer } from '../../common/context/useFormReducer'
import { StorageContext, ImageKey } from '../../../../services/indexedDBService'
import { submitQuestionnaireToGitHub } from '../../../../services/githubService'

// Extended context type with editor-specific methods
export interface EditorFormContextType extends FormContextType {
    hasUnsavedChanges: boolean;
    saveForm: (branchName: string) => Promise<void>;
    markAsSaved: () => void;
}

// Create context
const EditorFormContext = createContext<EditorFormContextType | undefined>(undefined)

// Local storage key for editor
const STORAGE_KEY = 'editor-form-data'

// Function to load initial data from localStorage
const loadInitialData = (): QuestionnaireFormData => {
    try {
        const savedData = localStorage.getItem(STORAGE_KEY)
        if (savedData) {
            const parsedData = JSON.parse(savedData)
            // Merge with initialFormData to ensure all required fields exist
            return {
                ...initialFormData,
                ...parsedData
            }
        }
    } catch (error) {
        console.error('Error loading editor data from localStorage:', error)
    }
    return initialFormData
}

// Context provider
interface EditorFormProviderProps {
    children: ReactNode
}

export const EditorFormProvider = ({ children }: EditorFormProviderProps) => {
    const [formData, dispatch] = useReducer(formReducer, loadInitialData())
    const lastSavedDataRef = useRef<string>(JSON.stringify(loadInitialData()))
    const hasUnsavedChangesRef = useRef<boolean>(false)
    const changedImageKeysRef = useRef<Set<ImageKey>>(new Set())

    // Calculate if there are unsaved changes
    const hasUnsavedChanges = JSON.stringify(formData) !== lastSavedDataRef.current

    // Save data to localStorage whenever formData changes
    useEffect(() => {
        try {
            const dataString = JSON.stringify(formData)
            const dataSizeInMB = new Blob([dataString]).size / (1024 * 1024)
            console.log(`Saving editor data to localStorage (${dataSizeInMB.toFixed(2)} MB)`)
            localStorage.setItem(STORAGE_KEY, dataString)
            console.log('Successfully saved editor data to localStorage')
        } catch (error) {
            console.error('Error saving editor data to localStorage:', error)
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                console.error('localStorage quota exceeded! Data size too large.')
                alert('Warning: Unable to save editor data - storage limit exceeded. Please use smaller images.')
            }
        }
    }, [formData])

    // Action handlers
    const updateBasicInfo = (data: Partial<QuestionnaireFormData['basicInfo']>) => {
        dispatch({ type: 'UPDATE_BASIC_INFO', payload: data })
    }

    const updateMainImages = (data: Partial<QuestionnaireFormData['mainImages']>) => {
        dispatch({ type: 'UPDATE_MAIN_IMAGES', payload: data })
    }

    const updateCeremonyLocation = (data: Partial<QuestionnaireFormData['ceremonyLocation']>) => {
        dispatch({ type: 'UPDATE_CEREMONY_LOCATION', payload: data })
    }

    const updateWeddingLocation = (data: Partial<QuestionnaireFormData['weddingLocation']>) => {
        dispatch({ type: 'UPDATE_WEDDING_LOCATION', payload: data })
    }

    const updateGuestsFAQ = (data: Partial<QuestionnaireFormData['guestsFAQ']>) => {
        dispatch({ type: 'UPDATE_GUESTS_FAQ', payload: data })
    }

    const addFAQItem = (faq: FAQItem) => {
        dispatch({ type: 'ADD_FAQ_ITEM', payload: faq })
    }

    const updateFAQAnswer = (id: string, answer: string) => {
        dispatch({ type: 'UPDATE_FAQ_ANSWER', payload: { id, answer } })
    }

    const removeFAQItem = (id: string) => {
        dispatch({ type: 'REMOVE_FAQ_ITEM', payload: id })
    }

    const updateWitnesses = (data: Partial<QuestionnaireFormData['witnesses']>) => {
        dispatch({ type: 'UPDATE_WITNESSES', payload: data })
    }

    const updateContact = (data: Partial<QuestionnaireFormData['contact']>) => {
        dispatch({ type: 'UPDATE_CONTACT', payload: data })
    }

    const updateDomain = (data: Partial<QuestionnaireFormData['domain']>) => {
        dispatch({ type: 'UPDATE_DOMAIN', payload: data })
    }

    const updateMenu = (data: Partial<QuestionnaireFormData['menu']>) => {
        dispatch({ type: 'UPDATE_MENU', payload: data })
    }

    const updateSchedule = (data: Partial<QuestionnaireFormData['schedule']>) => {
        dispatch({ type: 'UPDATE_SCHEDULE', payload: data })
    }

    const updateColors = (data: Partial<QuestionnaireFormData['colors']>) => {
        dispatch({ type: 'UPDATE_COLORS', payload: data })
    }

    const updateTables = (data: Partial<QuestionnaireFormData['tables']>) => {
        dispatch({ type: 'UPDATE_TABLES', payload: data })
    }

    const updateWeddingDetails = (data: Partial<QuestionnaireFormData['weddingDetails']>) => {
        dispatch({ type: 'UPDATE_WEDDING_DETAILS', payload: data })
    }

    const updateCurrentStep = (step: number) => {
        dispatch({ type: 'UPDATE_CURRENT_STEP', payload: step })
    }

    const loadFormData = useCallback((data: QuestionnaireFormData) => {
        dispatch({ type: 'LOAD_FROM_STORAGE', payload: data })
        // Update last saved data when loading from external source
        lastSavedDataRef.current = JSON.stringify(data)
    }, [])

    const resetForm = useCallback(() => {
        dispatch({ type: 'RESET_FORM' })
        lastSavedDataRef.current = JSON.stringify(initialFormData)
        changedImageKeysRef.current = new Set()
    }, [])

    const markImageChanged = useCallback((key: ImageKey) => {
        changedImageKeysRef.current.add(key)
        console.log(`Editor: Marked image as changed: ${key}`)
    }, [])

    /**
     * Manually saves form data to localStorage and GitHub
     * @param branchName - The GitHub branch name to save to
     */
    const saveForm = useCallback(async (branchName: string) => {
        try {
            console.log('Editor: Saving form data...')
            const imageKeysToSave = changedImageKeysRef.current
            
            console.log(`Editor: Changed images: ${imageKeysToSave.size > 0 ? Array.from(imageKeysToSave).join(', ') : 'none (skipping image push)'}`)
            
            await submitQuestionnaireToGitHub(formData, branchName, 'editor', imageKeysToSave)
            
            // Save to localStorage
            const dataString = JSON.stringify(formData)
            localStorage.setItem(STORAGE_KEY, dataString)
            
            // Update last saved snapshot and clear dirty images
            lastSavedDataRef.current = dataString
            changedImageKeysRef.current = new Set()
            
            console.log('Editor: Form data saved successfully')
        } catch (error) {
            console.error('Editor: Error saving form data:', error)
            throw error
        }
    }, [formData])

    /**
     * Marks the current state as saved (resets hasUnsavedChanges)
     */
    const markAsSaved = useCallback(() => {
        lastSavedDataRef.current = JSON.stringify(formData)
    }, [formData])

    const contextValue: EditorFormContextType = {
        formData,
        storageContext: 'editor' as StorageContext,
        hasUnsavedChanges,
        updateBasicInfo,
        updateMainImages,
        updateCeremonyLocation,
        updateWeddingLocation,
        updateGuestsFAQ,
        addFAQItem,
        updateFAQAnswer,
        removeFAQItem,
        updateWitnesses,
        updateContact,
        updateDomain,
        updateMenu,
        updateSchedule,
        updateColors,
        updateTables,
        updateWeddingDetails,
        updateCurrentStep,
        loadFormData,
        resetForm,
        markImageChanged,
        saveForm,
        markAsSaved
    }

    return (
        <EditorFormContext.Provider value={contextValue}>
            {children}
        </EditorFormContext.Provider>
    )
}

// Custom hook to use the editor context
export const useEditorForm = () => {
    const context = useContext(EditorFormContext)
    if (context === undefined) {
        throw new Error('useEditorForm must be used within an EditorFormProvider')
    }
    return context
}
